import { WatchOverlay } from '../clinica/WatchOverlay.js';
import { OutreachCapsule } from '../outreach/OutreachCapsule.js';
import { PertoReplyWatch } from '../perto/PertoReplyWatch.js';
import { InboxWatcher } from '../stage/InboxWatcher.js';
import { checkAppUpdate } from '../../lib/appUpdate.js';
import { devLiveScript } from '../../lib/voice/controller.js';
import { PLAN_CATALOG } from '@secretario/shared';
import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { DeskSide } from './DeskSide.js';
import { Link, useLocation } from 'wouter';
import { logout } from '../../lib/api/auth.js';
import {
  ASSISTANT_OPEN_EVENT,
  type AssistantOpenDetail,
  type AssistantPlaceRef,
} from '../../lib/assistantBus.js';
import { useBlockingSheetOpen } from '../../lib/blockingSheets.js';
import { PAYWALL_EVENT, useEntitlements, type PaywallReason } from '../../lib/plan.js';
import { PaywallSheet } from '../plan/PaywallSheet.js';
import { chime, DESK_PREF, useDeskMode } from '../../lib/deskMode.js';
import { useMicBusy } from '../../lib/micHold.js';
import { characterImage, characterName, DEFAULT_CHARACTER } from '../../lib/characters.js';
import { AI_CONSENT_EVENT, AiConsentSheet, hasAiConsent } from '../assistant/AiConsentSheet.js';
import { AssistantModal } from '../assistant/AssistantModal.js';
import { takeInvite, takeReturnPath } from '../../lib/invite.js';
import { onIncomingMessage } from '../../lib/notify.js';
import { useSession } from '../../lib/session.js';
import { getPrivacy, parsePrivacy, setPrivacy } from '../../lib/matrix/privacy.js';
import { syncMutedRooms } from '../../lib/notify.js';
import { API_BASE } from '../../lib/api/http.js';
import { setUnreadCount } from '../../lib/unreadCount.js';
import { nativeBridge } from '../../lib/secretarioNative.js';
import { hapticSuccess } from '../../lib/native.js';
import { setSecretaryTargetName, watchRoomNames } from '../../lib/roomNames.js';
import {
  pickConversationFor,
  SECRETARY_ROOM,
  setSecretaryShare,
  setShareDraft,
  setShareFiles,
  SHARE_PICK_EVENT,
  type SharePickDetail,
} from '../../lib/shareDraft.js';
import { filesFromNative } from '../../lib/sharedFiles.js';
import { closeAllLayers } from '../../lib/layers.js';
import { ASSISTANT_ROUTE } from '../../lib/assistantBus.js';
import { ShareSheet } from '../messaging/ShareSheet.js';
import { useProfile } from '../../lib/useProfile.js';
import { BEARDED_CHARACTERS, LOOK_PREF } from '@secretario/shared';
import { patchProfile } from '../../lib/api/profile.js';
import { initialLook, LOOK_CHANGED, refreshLookOnReturn, syncLook } from '../../lib/look.js';
import { PROFILE_KEY } from '../../lib/useProfile.js';
import { useQueryClient } from '@tanstack/react-query';
import type { SecretaryIdentity } from '../../lib/autoSecretary.js';
import { StageProvider, useOptionalStage } from '../stage/StageProvider.js';
import { LIVE_OPEN_EVENT } from '../../lib/liveBus.js';
import { useStageOn } from '../../lib/stage/useStageOn.js';

const LiveVoiceOverlay = lazy(() =>
  import('../voice/LiveVoiceOverlay.js').then((module) => ({ default: module.LiveVoiceOverlay })),
);
const Palco = lazy(() => import('../stage/Palco.js').then((m) => ({ default: m.Palco })));
const PeoplePanel = lazy(() =>
  import('../stage/PeoplePanel.js').then((m) => ({ default: m.PeoplePanel })),
);
const TutorialCoach = lazy(() =>
  import('../stage/TutorialCoach.js').then((m) => ({ default: m.TutorialCoach })),
);
const StageLayers = lazy(() =>
  import('../stage/StageLayers.js').then((m) => ({ default: m.StageLayers })),
);
const RecoverySheet = lazy(() =>
  import('../messaging/RecoverySheet.js').then((m) => ({ default: m.RecoverySheet })),
);
const MePhoto = lazy(() => import('./PersonPhoto.js'));
const VerificationSheet = lazy(() =>
  import('../messaging/VerificationSheet.js').then((m) => ({ default: m.VerificationSheet })),
);
import './chrome.css';
import { useHomeMode } from '../../lib/homeMode.js';
import { HomeModeSwitch } from './HomeModeSwitch.js';
import { ClinicHomeGate } from '../clinica/gate.js';
import { LayerBoundary } from './ErrorBoundary.js';
import { claimOpeningNotice } from '../../lib/stage/openingBudget.js';
import { onTalkRequest } from '../../lib/liveService.js';
import { GlyphLifebuoy } from '../ui/Glyphs.js';

// Ícones da navegação inferior (traço simples, estilo app)
const IcHome = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
    <path
      d="M4 11.2 12 5l8 6.2V19a1.4 1.4 0 0 1-1.4 1.4h-3.2V14h-4.8v6.4H5.4A1.4 1.4 0 0 1 4 19z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
  </svg>
);
const IcChat = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
    <path
      d="M4.5 6.5A2.5 2.5 0 0 1 7 4h10a2.5 2.5 0 0 1 2.5 2.5v7A2.5 2.5 0 0 1 17 16h-5.6L7.5 19.4V16H7a2.5 2.5 0 0 1-2.5-2.5z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
    <path d="M8.5 9h7M8.5 12h4.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);
const IcPeople = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
    <circle cx="9" cy="8" r="3.1" fill="none" stroke="currentColor" strokeWidth="1.7" />
    <path
      d="M3.5 19c0-3 2.6-4.6 5.5-4.6s5.5 1.6 5.5 4.6"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
    <path
      d="M16 5.6a2.9 2.9 0 0 1 0 5.4m1 3.6c2 .5 3.5 1.9 3.5 4.4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
  </svg>
);
const IcCal = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
    <rect
      x="4"
      y="5.5"
      width="16"
      height="15"
      rx="2.6"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
    />
    <path
      d="M4 10h16M8 3.5v3.5M16 3.5v3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
  </svg>
);
const IcMic = () => (
  <svg viewBox="0 0 24 24" width="25" height="25" aria-hidden="true">
    <rect x="9" y="3" width="6" height="12" rx="3" fill="currentColor" />
    <path
      d="M6 11a6 6 0 0 0 12 0M12 19v2"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      fill="none"
    />
  </svg>
);

const TABS = [
  {
    href: '/',
    label: 'Tela inicial',
    Icon: IcHome,
    match: (l: string) => l === '/' || l.startsWith('/habilidade'),
  },
  {
    href: '/conversas',
    label: 'Conversas',
    Icon: IcChat,
    match: (l: string) => l.startsWith('/conversas'),
  },
  {
    href: '/contatos',
    label: 'Contatos',
    Icon: IcPeople,
    match: (l: string) => l.startsWith('/contatos'),
  },
  { href: '/agenda', label: 'Agenda', Icon: IcCal, match: (l: string) => l.startsWith('/agenda') },
];

const HOME_PATHS = new Set(['/']);

/**
 * "Caderno" no menu do avatar: com o palco ligado, o que o Secretário sabe da
 * pessoa abre como painel, por cima de onde ela estiver.
 */
function NotebookMenuItem({ onPick }: { onPick: () => void }) {
  const stage = useOptionalStage();
  const [, navigate] = useLocation();
  return (
    <button
      type="button"
      className="psn-menu-it"
      role="menuitem"
      onClick={() => {
        onPick();
        if (stage) stage.show({ kind: 'notebook' });
        else navigate('/sobre-voce');
      }}
    >
      <span className="psn-menu-e">
        <img src="/owner-facts/voce.webp" alt="" />
      </span>
      <span className="psn-menu-t">
        <b>Caderno</b>
        <small>O que eu sei de você</small>
      </span>
    </button>
  );
}

export function AppChrome({ children }: { children: ReactNode }) {
  const [location, navigate] = useLocation();
  // A Ajuda sabe de onde a pessoa veio (só a rota, nunca o conteúdo).
  useEffect(() => {
    if (location.startsWith('/ajuda')) return;
    try {
      window.sessionStorage.setItem('secretario.ajuda.de', location);
    } catch {
      /* sem memória */
    }
  }, [location]);
  const session = useSession();
  const profileQ = useProfile();
  // Privacidade das conversas: o que está no perfil vale neste aparelho.
  const privacyPrefs = profileQ.data?.preferences.privacy;
  useEffect(() => {
    if (profileQ.data) setPrivacy(parsePrivacy(privacyPrefs));
  }, [profileQ.data, privacyPrefs]);
  // O atalho do Secretário no "compartilhar" do Android leva o nome dele.
  const shareName = profileQ.data?.secretaryName ?? '';
  useEffect(() => setSecretaryTargetName(shareName), [shareName]);
  // O mensageiro liga logo depois da abertura (fora do primeiro desenho): presença,
  // recebimento e o selo de não lidas valem em qualquer tela.
  const [unread, setUnread] = useState(0);
  // Os painéis do palco (a faixa de atalhos do Hoje) leem a mesma contagem.
  useEffect(() => setUnreadCount(unread), [unread]);
  const queryClient = useQueryClient();
  // Visual vivo (barba do personagem a traço): o estágio sai do perfil; a primeira vez
  // começa a contar os dias; depois de "faz a barba" o perfil volta e o palco anima.
  const lookPrefs = profileQ.data?.preferences[LOOK_PREF];
  const bearded = (BEARDED_CHARACTERS as readonly string[]).includes(
    profileQ.data?.characterKey ?? '',
  );
  useEffect(() => {
    if (!profileQ.data) return;
    syncLook(profileQ.data.preferences);
    if (bearded && lookPrefs === undefined) {
      void patchProfile({ preferences: { [LOOK_PREF]: initialLook() } })
        .then((profile) => queryClient.setQueryData(PROFILE_KEY, profile))
        .catch(() => {});
    }
  }, [profileQ.data, lookPrefs, bearded, queryClient]);
  useEffect(() => {
    const changed = () => void queryClient.invalidateQueries({ queryKey: PROFILE_KEY });
    const visible = () => {
      if (document.visibilityState === 'visible') refreshLookOnReturn();
    };
    window.addEventListener(LOOK_CHANGED, changed);
    document.addEventListener('visibilitychange', visible);
    return () => {
      window.removeEventListener(LOOK_CHANGED, changed);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [queryClient]);
  // Quem é o Secretário deste dono, para as falas que ele publica sozinho.
  const secretaryRef = useRef<SecretaryIdentity | null>(null);
  useEffect(() => {
    const profile = profileQ.data;
    secretaryRef.current = profile
      ? {
          name: profile.secretaryName?.trim() || 'Secretário',
          characterKey: profile.characterKey,
          ownerName: profile.nickname?.trim() || session?.user.displayName.split(' ')[0] || '',
        }
      : null;
  }, [profileQ.data, session]);
  const userId = session?.user.id;
  const ownUsername = session?.user.username;
  // Veio de uma página pública sem conta (clínica, loja, confirmar): volta para ela assim que a
  // conta entra, sem esperar o mensageiro (que pode demorar ou faltar).
  useEffect(() => {
    if (!userId) return;
    const back = takeReturnPath();
    if (back) navigate(back);
  }, [userId, navigate]);

  useEffect(() => {
    if (!userId) return;
    let stop: (() => void) | undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      void Promise.all([
        import('../../lib/matrix/client.js'),
        import('../../lib/matrix/unread.js'),
      ]).then(async ([clientModule, unreadModule]) => {
        const client = await clientModule.ensureMatrixClient();
        if (!client || cancelled) return;
        const stopUnread = unreadModule.watchUnread(client, setUnread);
        const stopIncoming = unreadModule.watchIncoming(client, onIncomingMessage);
        const [delivery, auto, proxy] = await Promise.all([
          import('../../lib/matrix/delivery.js'),
          import('../../lib/autoSecretary.js'),
          import('../../lib/proxySecretary.js'),
        ]);
        // A tela pode ter saído enquanto os módulos carregavam: nada de vigia órfão.
        if (cancelled) {
          stopUnread();
          stopIncoming();
          return;
        }
        const stopDelivery = delivery.watchDelivery(client);
        // Avisos nativos: sessão para "marcar como lida", conversas silenciadas e
        // o que a pessoa fez no aviso (abrir a conversa, responder).
        void nativeBridge.setSession({
          homeserverUrl: client.getHomeserverUrl(),
          accessToken: client.getAccessToken() ?? '',
          privateReceipts: !getPrivacy().readReceipts,
          ownerName: secretaryRef.current?.ownerName || 'Você',
          apiBase: new URL(API_BASE, window.location.href).href,
        });
        void syncMutedRooms();
        const stopNames = watchRoomNames(client);
        const stopNative = await nativeBridge.watch({
          onOpen: (roomId) => navigate(`/conversas/${encodeURIComponent(roomId)}`),
          onReply: (roomId, text) => {
            navigate(`/conversas/${encodeURIComponent(roomId)}`);
            void import('../../lib/matrix/rooms.js')
              .then((rooms) => rooms.sendText(client, roomId, text))
              .catch(() => {});
          },
          onMuted: () => void syncMutedRooms(),
          onNotice: (href) => navigate(href),
          // Compartilhado de outro app: direto no Secretário ou na conversa do atalho, ou a
          // pessoa escolhe para quem mandar. Arquivos chegam copiados pelo Android.
          onShare: (text, roomId, nativeFiles) => {
            void filesFromNative(nativeFiles).then(({ files, notice }) => {
              setShareNotice(notice);
              if (!text && files.length === 0) {
                // Nada deu para abrir: avisa em vez de ficar calado.
                setShareToast(notice ?? 'Não consegui abrir o que foi compartilhado.');
                return;
              }
              closeAllLayers();
              if (roomId === SECRETARY_ROOM) {
                setSecretaryShare(text, files);
                navigate(ASSISTANT_ROUTE);
              } else if (roomId) {
                setShareDraft(roomId, text);
                setShareFiles(roomId, files);
                navigate(`/conversas/${encodeURIComponent(roomId)}`);
              } else {
                pickConversationFor(text, files);
              }
            });
          },
        });
        const stopAuto = auto.watchAutoSecretary(
          client,
          () => secretaryRef.current,
          () => void queryClient.invalidateQueries({ queryKey: ['approvals'] }),
        );
        // Resposta por procuração e o sinal de "estou com o app aberto".
        const stopProxy = proxy.watchProxySecretary(client, () => secretaryRef.current);
        stop = () => {
          stopUnread();
          stopIncoming();
          stopDelivery();
          stopNative();
          stopNames();
          stopAuto();
          stopProxy();
        };
        // Liga os avisos com o app fechado. Quem libera a permissão depois (pelo
        // app ou pelas configurações do aparelho) é registrado ao voltar para o app.
        const linkPush = () =>
          void import('../../lib/push.js')
            .then((push) => push.registerPush(client, navigate))
            .catch(() => {});
        linkPush();
        const onVisible = () => {
          if (document.visibilityState === 'visible') linkPush();
        };
        document.addEventListener('visibilitychange', onVisible);
        const stopBefore = stop;
        stop = () => {
          stopBefore?.();
          document.removeEventListener('visibilitychange', onVisible);
        };
        // Chegou por um convite: a conversa com quem convidou já nasce aberta.
        const invited = takeInvite();
        if (invited && invited !== ownUsername) {
          try {
            await import('../../lib/api/business.js')
              .then((business) => business.visitBusiness(invited))
              .catch(() => {});
            const rooms = await import('../../lib/matrix/rooms.js');
            const roomId = await rooms.startDirectMessage(
              client,
              `@${invited}:${client.getDomain()}`,
            );
            if (!cancelled) navigate(`/conversas/${encodeURIComponent(roomId)}`);
          } catch {
            /* quem convidou não foi encontrado: segue a vida normal do app */
          }
        }
      });
    }, 1200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      stop?.();
    };
  }, [userId]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [assistant, setAssistant] = useState<{
    listen: boolean;
    prefill: string;
    lugar?: AssistantPlaceRef | undefined;
  } | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const characterKey = profileQ.data?.characterKey ?? DEFAULT_CHARACTER;
  const avatarImg = characterImage(characterKey, 'simpatico');
  const waveImg = characterImage(characterKey, 'divertido');
  const first =
    (profileQ.data?.nickname ?? session?.user.displayName ?? '').trim().split(/\s+/)[0] ?? '';

  // Fecha o balão ao tocar fora / Esc.
  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  useEffect(() => setMenuOpen(false), [location]);

  // Qualquer tela pode abrir o Secretário (ex.: sugestões do "Sobre você").
  useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<AssistantOpenDetail>).detail ?? {};
      setAssistant({
        listen: detail.listen === true,
        prefill: detail.prefillText ?? '',
        lugar: detail.lugar,
      });
    };
    window.addEventListener(ASSISTANT_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(ASSISTANT_OPEN_EVENT, onOpen);
  }, [navigate]);

  // Permissão de IA: aparece uma vez depois da entrada e sempre que o servidor pedir.
  const [aiSheet, setAiSheet] = useState(false);
  const otherSheetOpen = useBlockingSheetOpen();
  const aiAllowed = hasAiConsent(profileQ.data?.preferences);
  const profileReady = !!profileQ.data;
  useEffect(() => {
    if (!profileReady || aiAllowed) return;
    let asked = false;
    try {
      asked = window.sessionStorage.getItem('secretario.permissao-ia.vista') === '1';
    } catch {
      /* sem armazenamento: pergunta de novo na próxima abertura */
    }
    if (!asked) setAiSheet(true);
  }, [profileReady, aiAllowed]);
  // Só conta como vista quando a pessoa fecha a folha (ela pode ter esperado outra folha sair).
  const closeAiSheet = () => {
    try {
      window.sessionStorage.setItem('secretario.permissao-ia.vista', '1');
    } catch {
      /* segue sem lembrar */
    }
    setAiSheet(false);
  };
  useEffect(() => {
    const open = () => setAiSheet(true);
    window.addEventListener(AI_CONSENT_EVENT, open);
    return () => window.removeEventListener(AI_CONSENT_EVENT, open);
  }, []);

  // Folha de planos: qualquer tela abre, dizendo em que momento a pessoa esbarrou.
  const entitlementsQ = useEntitlements(!!userId);
  const isPro = entitlementsQ.data?.plan === 'pro';
  // Conversa ao vivo: tocar e segurar o microfone do rodapé (só quando o plano e o servidor liberam).
  const [live, setLive] = useState(false);
  const [holding, setHolding] = useState(false);
  const [liveHint, setLiveHint] = useState(false);
  const press = useRef<{ timer?: ReturnType<typeof setTimeout>; long: boolean }>({ long: false });
  const liveEnabled =
    (entitlementsQ.data?.features.voice.enabled ?? false) ||
    // Só no desenvolvimento: o roteiro de teste do ao vivo abre sem a voz liberada.
    (import.meta.env.DEV && !!devLiveScript());
  const stageOn = useStageOn(!!userId);
  const startHold = () => {
    if (!liveEnabled) return;
    press.current.long = false;
    setHolding(true);
    clearTimeout(press.current.timer);
    press.current.timer = setTimeout(() => {
      press.current.long = true;
      setHolding(false);
      setLiveHint(false);
      hapticSuccess();
      setLive(true);
    }, 450);
  };
  const stopHold = () => {
    clearTimeout(press.current.timer);
    setHolding(false);
  };
  // Outra tela pediu a conversa por voz (ex.: o passo da cartela): abre ao vivo quando
  // está liberada; sem ela, o pedido rápido já ouvindo.
  useEffect(() => {
    const open = () => {
      if (liveEnabled) {
        hapticSuccess();
        setLive(true);
      } else {
        setAssistant({ listen: true, prefill: '' });
      }
    };
    window.addEventListener(LIVE_OPEN_EVENT, open);
    // Atalho "Falar" do ícone do app e widget da tela inicial (Android).
    let stopTalk = () => {};
    void onTalkRequest(open).then((stop) => {
      stopTalk = stop;
    });
    return () => {
      window.removeEventListener(LIVE_OPEN_EVENT, open);
      stopTalk();
    };
  }, [liveEnabled]);
  // Uma dica, uma vez: depois que a pessoa já usou, não aparece mais. Com o palco
  // ligado a dica não existe (o Falar está à vista) e não pode gastar a vaga do
  // aviso de abertura, que é do Hoje e da espiada da alça.
  useEffect(() => {
    if (!liveEnabled || stageOn) return;
    let seen = false;
    try {
      seen = window.localStorage.getItem('secretario.dica-ao-vivo') === '1';
    } catch {
      /* sem armazenamento: mostra a dica */
    }
    if (seen) return;
    // A dica só conta como vista quando aparece; com outro aviso nesta abertura, fica para a próxima.
    const show = setTimeout(() => {
      if (!claimOpeningNotice('live-hint')) return;
      try {
        window.localStorage.setItem('secretario.dica-ao-vivo', '1');
      } catch {
        /* segue sem lembrar */
      }
      setLiveHint(true);
    }, 1800);
    const hide = setTimeout(() => setLiveHint(false), 8000);
    return () => {
      clearTimeout(show);
      clearTimeout(hide);
    };
  }, [liveEnabled, stageOn]);

  const [paywall, setPaywall] = useState<{ reason: PaywallReason; surface: string } | null>(null);
  useEffect(() => {
    const open = (event: Event) => {
      const detail = (event as CustomEvent<{ reason: PaywallReason; surface: string }>).detail;
      if (detail?.reason) setPaywall({ reason: detail.reason, surface: detail.surface || 'app' });
    };
    window.addEventListener(PAYWALL_EVENT, open);
    return () => window.removeEventListener(PAYWALL_EVENT, open);
  }, []);

  // "Mandar para": o que foi compartilhado (texto e arquivos) esperando a escolha.
  const [share, setShare] = useState<{ text: string; files: File[] } | null>(null);
  const [shareNotice, setShareNotice] = useState<string | null>(null);
  const [shareToast, setShareToast] = useState<string | null>(null);
  // Versão nova na loja: o Secretário já postou na conversa; o toque leva até lá.
  const [updateToast, setUpdateToast] = useState<string | null>(null);
  useEffect(() => {
    if (!userId) return;
    let alive = true;
    void checkAppUpdate().then((result) => {
      if (!alive || !result?.posted) return;
      void queryClient.invalidateQueries({ queryKey: ['assistant', 'history'] });
      setUpdateToast(`Novidades da versão ${result.versionName ?? 'nova'}: toque para ver`);
    });
    return () => {
      alive = false;
    };
  }, [userId]);
  useEffect(() => {
    if (!updateToast) return;
    const timer = setTimeout(() => setUpdateToast(null), 8000);
    return () => clearTimeout(timer);
  }, [updateToast]);
  useEffect(() => {
    if (!shareToast) return;
    const timer = setTimeout(() => setShareToast(null), 4200);
    return () => clearTimeout(timer);
  }, [shareToast]);
  useEffect(() => {
    const open = (event: Event) => {
      const detail = (event as CustomEvent<SharePickDetail>).detail;
      if (detail?.text || detail?.files?.length)
        setShare({ text: detail.text ?? '', files: detail.files ?? [] });
    };
    window.addEventListener(SHARE_PICK_EVENT, open);
    return () => window.removeEventListener(SHARE_PICK_EVENT, open);
  }, []);

  // Objetos 3D da mesma família das habilidades; "Mudar o jeito" mostra o próprio personagem.
  const menuItems = [
    {
      href: '/sobre-voce',
      art: '/owner-facts/voce.webp',
      label: 'Sobre você',
      sub: 'O que eu sei de você',
    },
    {
      href: '/mudar-jeito',
      art: avatarImg,
      label: 'Mudar o jeito',
      sub: 'Personagem e personalidade',
    },
    {
      href: '/ajustes',
      art: '/menu/ajustes.webp',
      label: 'Conta',
      sub: 'Plano e uso, notificações, privacidade',
    },
    {
      href: '/negocio',
      art: '/habilidades/negocio.webp',
      label: 'Meu negócio',
      sub: 'Atendimento dos seus clientes',
    },
  ];

  const isHome = HOME_PATHS.has(location);
  // Quem tem clínica: no horário da clínica a Início é o dia da clínica (fase E).
  const homeMode = useHomeMode();
  const clinicHome = isHome && homeMode.available && homeMode.mode === 'clinica';
  const micInUse = useMicBusy();
  // "Deixar na mesa": em qualquer tela, com a chave ligada, sem conversa ao vivo aberta,
  // sem o pedido rápido aberto e sem outro recurso usando o microfone (ditado, áudio).
  const deskPrefs = profileQ.data?.preferences ?? {};
  const deskName =
    profileQ.data?.secretaryName?.trim() || characterName(characterKey) || 'Secretário';
  const desk = useDeskMode({
    enabled:
      stageOn && !live && !assistant && !micInUse && liveEnabled && deskPrefs[DESK_PREF] === true,
    keepAwake: isHome,
    names: [deskName],
    onWake: () => {
      hapticSuccess();
      chime();
      setLive(true);
    },
  });
  // Início em palco: sem barra de abas; o personagem é a casa e as páginas entram por cima.
  useEffect(() => {
    document.documentElement.classList.toggle('stage-on', stageOn);
    return () => document.documentElement.classList.remove('stage-on');
  }, [stageOn]);
  // Fora da Início não há a faixa do palco: os cartões descem para o rodapé, sem cobrir a página.
  useEffect(() => {
    document.documentElement.classList.toggle('stg-inner', !isHome);
    return () => document.documentElement.classList.remove('stg-inner');
  }, [isHome]);
  // Numa conversa, o rodapé é o campo de escrever: o cartão fica logo acima dele.
  const inChat = /^\/conversas\/.+/.test(location);
  useEffect(() => {
    document.documentElement.classList.toggle('stg-chat', inChat);
    return () => document.documentElement.classList.remove('stg-chat');
  }, [inChat]);
  const secretaryName = profileQ.data?.secretaryName?.trim() || 'Secretário';
  const talk = {
    holding,
    liveEnabled,
    start: startHold,
    stop: stopHold,
    tap: () => {
      // Segurou: a conversa ao vivo já abriu; o toque que sobra não abre o pedido rápido.
      if (press.current.long) {
        press.current.long = false;
        return;
      }
      setAssistant({ listen: true, prefill: '' });
    },
  };
  // Numa conversa aberta o compositor assume o lugar da navegação (MACRO §5).
  const inThread = /^\/conversas\/.+/.test(location);
  // A mesa do pessoal: a classe no <html> tira a barra de baixo e abre espaço à esquerda.
  const deskSide = !stageOn && Boolean(userId);
  useEffect(() => {
    if (!deskSide) return;
    document.documentElement.classList.add('ds-on');
    return () => document.documentElement.classList.remove('ds-on');
  }, [deskSide]);
  const peopleRoute = stageOn && (location === '/conversas' || location === '/contatos');

  const shell = (
    <div
      className={`app${stageOn ? ' stage-on' : ''}${stageOn && isHome && !clinicHome ? ' stage-home' : ''}${peopleRoute ? ' stage-people' : ''}`}
    >
      <header className="psn-top">
        {stageOn && !isHome ? (
          <Link href="/" className="psn-brand" aria-label={`Voltar para ${secretaryName}`}>
            <img className="psn-seal" src={avatarImg} alt="" /> {secretaryName}
          </Link>
        ) : (
          <Link href="/" className="psn-brand" aria-label="Secretário, ir para o início">
            <span className={`psn-mark${isPro ? ' pro' : ''}`}>
              <img className="psn-logo" src="/icon-app.png" alt="" />
            </span>
            <span className="psn-name">
              Secretário
              {isPro && <small className="psn-plan">{PLAN_CATALOG.pro.name}</small>}
            </span>
          </Link>
        )}
        {isHome && homeMode.showSwitch && (
          <HomeModeSwitch
            mode={homeMode.mode}
            options={homeMode.options}
            clinicName={homeMode.clinicName}
            onPick={homeMode.choose}
          />
        )}
        <div className="psn-mewrap" ref={menuRef}>
          <button
            className={`psn-me${menuOpen ? ' open' : ''}`}
            aria-label="Sua conta"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <span className="psn-me-av has-photo-slot">
              <img src={avatarImg} alt="" />
              <Suspense fallback={null}>
                <MePhoto />
              </Suspense>
            </span>
          </button>
          {menuOpen && (
            <div className="psn-menu" role="menu">
              <Link href="/perfil" className="psn-menu-hd" role="menuitem">
                <span className="psn-menu-av">
                  <img src={waveImg} alt="" />
                </span>
                <span className="psn-menu-hi">
                  <b>{first ? `Oi, ${first}!` : 'Sua conta'}</b>
                  <small>Ver meu perfil ›</small>
                </span>
              </Link>
              {/* No palco, "Sobre você" vira o Caderno (painel); a página antiga continua existindo. */}
              {stageOn && <NotebookMenuItem onPick={() => setMenuOpen(false)} />}
              {(stageOn ? menuItems.slice(1) : menuItems).map((it) => (
                <Link key={it.href} href={it.href} className="psn-menu-it" role="menuitem">
                  <span className="psn-menu-e">
                    <img src={it.art} alt="" />
                  </span>
                  <span className="psn-menu-t">
                    <b>{it.label}</b>
                    <small>{it.sub}</small>
                  </span>
                </Link>
              ))}
              {/* Ajuda no rodapé, junto de Sair: é apoio, não um destino do dia a dia. */}
              <Link href="/ajuda" className="psn-menu-help" role="menuitem">
                <GlyphLifebuoy />
                <span>
                  <b>Ajuda</b>
                  <small>Tire dúvidas comigo ou fale com uma pessoa</small>
                </span>
              </Link>
              <button className="psn-menu-out" role="menuitem" onClick={() => void logout()}>
                Sair
              </button>
            </div>
          )}
        </div>
      </header>

      <LayerBoundary
        where="pedido-rapido"
        onFail={() => setAssistant(null)}
        resetKey={assistant !== null}
      >
        <AssistantModal
          open={assistant !== null}
          autoListen={assistant?.listen ?? false}
          prefillText={assistant?.prefill ?? ''}
          lugar={assistant?.lugar ?? null}
          onClose={() => setAssistant(null)}
        />
      </LayerBoundary>
      <Suspense fallback={null}>
        <RecoverySheet />
        <VerificationSheet />
      </Suspense>
      <main className={`app-main${isHome && !clinicHome ? ' app-main-track' : ''}`}>
        {stageOn ? (
          <>
            <Suspense fallback={null}>
              <Palco
                visible={(isHome && !clinicHome) || peopleRoute}
                people={peopleRoute}
                live={live}
                unread={unread}
                talk={talk}
                desk={desk}
                onWrite={() => setAssistant({ listen: false, prefill: '' })}
              />
            </Suspense>
            {peopleRoute && (
              // Conversas e Contatos: a folha sobe sobre o mesmo rosto vivo do palco.
              // Chave fixa: trocar de aba não remonta nada.
              <Suspense fallback={null}>
                <PeoplePanel
                  key="people"
                  segment={location === '/contatos' ? 'contatos' : 'conversas'}
                  unread={unread}
                />
              </Suspense>
            )}
            {clinicHome && (
              <div className="stg-page" key="clinica-inicio">
                <ClinicHomeGate />
              </div>
            )}
            {!isHome && !peopleRoute && (
              // A chave por rota faz cada página entrar da direita, não só a primeira.
              <div className="stg-page" key={location}>
                {children}
              </div>
            )}
          </>
        ) : clinicHome ? (
          <ClinicHomeGate />
        ) : (
          children
        )}
      </main>

      {stageOn && (
        <LayerBoundary where="paineis" resetKey={location}>
          <Suspense fallback={null}>
            <StageLayers />
            <TutorialCoach />
            <InboxWatcher />
          </Suspense>
        </LayerBoundary>
      )}
      {/* Na mesa (1024+), a barra lateral faz o papel da barra de baixo (leva 1, D9). */}
      {!stageOn &&
        userId &&
        createPortal(
          <DeskSide
            location={location}
            unread={unread}
            isPro={isPro}
            onTalk={() => setAssistant({ listen: false, prefill: '' })}
          />,
          document.body,
        )}
      {!stageOn &&
        !inThread &&
        createPortal(
          <nav className="psn-nav" aria-label="Navegação">
            {TABS.slice(0, 2).map(({ href, label, Icon, match }) => (
              <Link key={href} href={href} className={`psn-tab${match(location) ? ' on' : ''}`}>
                <span className="psn-ic">
                  <Icon />
                  {href === '/conversas' && unread > 0 && (
                    <em className="psn-badge" aria-label={`${unread} não lidas`}>
                      {unread > 99 ? '99+' : unread}
                    </em>
                  )}
                </span>
                <span>{label}</span>
              </Link>
            ))}
            <div className="psn-tab psn-tab-mic">
              <span className={`psn-mic-ring${holding ? ' holding' : ''}`}>
                {liveHint && (
                  <span className="psn-live-hint" role="status">
                    Segure para conversar ao vivo
                  </span>
                )}
                <button
                  className="psn-mic"
                  onPointerDown={startHold}
                  onPointerUp={stopHold}
                  onPointerLeave={stopHold}
                  onPointerCancel={stopHold}
                  onContextMenu={(event) => event.preventDefault()}
                  onClick={() => {
                    // Segurou: a conversa ao vivo já abriu; o toque que sobra não abre o modal.
                    if (press.current.long) {
                      press.current.long = false;
                      return;
                    }
                    // Toque simples: a conversa especial, já ouvindo.
                    setAssistant({ listen: true, prefill: '' });
                  }}
                  aria-label="Falar com o Secretário"
                  aria-description={
                    liveEnabled
                      ? 'Toque para falar; toque e segure para conversar ao vivo'
                      : undefined
                  }
                >
                  <IcMic />
                </button>
              </span>
            </div>
            {TABS.slice(2).map(({ href, label, Icon, match }) => (
              <Link key={href} href={href} className={`psn-tab${match(location) ? ' on' : ''}`}>
                <span className="psn-ic">
                  <Icon />
                </span>
                <span>{label}</span>
              </Link>
            ))}
          </nav>,
          document.body,
        )}
      {/* O modo Assistir: a tela repete a ação que o Secretário fez (F1/F3), em qualquer página. */}
      <WatchOverlay />
      {/* O envio em lote minimizado: a cápsula (celular) ou o cartão no canto (mesa). */}
      <OutreachCapsule enabled={!!userId} />
      {/* Perto de Mim (PA2): a resposta do negócio pelo aviso do WhatsApp entra no pedido. */}
      <PertoReplyWatch enabled={!!userId} />
      {live && (
        <LayerBoundary where="ao-vivo" onFail={() => setLive(false)}>
          <Suspense fallback={null}>
            <LiveVoiceOverlay onClose={() => setLive(false)} inStage={stageOn} />
          </Suspense>
        </LayerBoundary>
      )}
      {shareToast && (
        <div className="app-toast" role="status">
          {shareToast}
        </div>
      )}
      {updateToast && !shareToast && (
        <button
          type="button"
          className="app-toast app-toast-tap"
          onClick={() => {
            setUpdateToast(null);
            navigate(ASSISTANT_ROUTE);
          }}
        >
          {updateToast}
        </button>
      )}
      {share && (
        <ShareSheet
          text={share.text}
          files={share.files}
          notice={shareNotice}
          onClose={() => {
            setShare(null);
            setShareNotice(null);
          }}
        />
      )}
      {paywall && (
        <PaywallSheet
          reason={paywall.reason}
          surface={paywall.surface}
          characterKey={characterKey}
          onClose={() => setPaywall(null)}
        />
      )}
      {aiSheet && !aiAllowed && !otherSheetOpen && (
        <AiConsentSheet
          characterKey={characterKey}
          secretaryName={profileQ.data?.secretaryName?.trim() || 'seu Secretário'}
          onClose={closeAiSheet}
        />
      )}
    </div>
  );
  return stageOn ? <StageProvider>{shell}</StageProvider> : shell;
}
